/* SESE YOLO | Automatic 1/2/3 option dropdown layout
   Standalone enhancement for existing SESE Bundle Builder v1.8.
   The original [data-sese-variant-select] remains the source of truth for cart, price and image.
   No product-specific code or option-name assumptions.
*/
(() => {
  'use strict';
  if (window.__SESE_GLOBAL_OPTION_SPLIT_V1__) return;
  window.__SESE_GLOBAL_OPTION_SPLIT_V1__ = true;

  const getParts = variant => Array.isArray(variant && variant.options) ? variant.options.map(v => String(v)) : [];
  const looksLikeColor = name => /(^|\W)(color|colour|couleur|farbe|shade)(\W|$)/i.test(String(name || ''));
  const hexFor = name => ({
    black:'#202020', white:'#ffffff', blue:'#1763d1', navy:'#142857', red:'#e34b47',
    green:'#34aa68', orange:'#ef8e3f', pink:'#e885ad', yellow:'#e9c632',
    purple:'#9363cf', grey:'#979aa2', gray:'#979aa2', brown:'#936144',
    beige:'#d6c0a0', gold:'#c9a042', silver:'#c2c9d1'
  })[String(name || '').trim().toLowerCase()] || '#c6cbd2';

  function readOptions(root) {
    const node = root.querySelector('[data-sese-option-names]');
    if (!node) return [];
    try {
      const names = JSON.parse(node.textContent);
      return Array.isArray(names) ? names.map(v => String(v)) : [];
    } catch (error) {
      console.warn('SESE options: Product option names could not be read.', error);
      return [];
    }
  }

  function enhanceRoot(root) {
    // Core builder initializes the data map first. If it is not ready, leave everything as is.
    if (!(root._seseVariantById instanceof Map)) return false;
    const names = readOptions(root);
    const count = names.length;
    // One option (or default title): preserve native full-width variant selector unchanged.
    if (count < 2 || count > 3) return true;

    const variants = [...root._seseVariantById.values()];
    const available = variants.filter(v => v.available && getParts(v).length === count);
    if (!available.length) return true;

    root.querySelectorAll('[data-sese-offer]').forEach(offer => {
      const list = offer.querySelector('[data-sese-unit-variants]');
      if (!list) return;
      const rows = [...list.querySelectorAll('.sese-bb__variant-item')];
      if (!rows.length) return;
      let enhanced = false;

      rows.forEach((row, rowIndex) => {
        if (row.dataset.seseSplitReady === 'true') { enhanced = true; return; }
        const original = row.querySelector('select[data-sese-variant-select]');
        const field = row.querySelector('.sese-bb__variant-field');
        if (!original || !field) return;

        const allowedIds = new Set(
          [...original.options].filter(o => !o.disabled).map(o => String(o.value))
        );
        const permitted = available.filter(v => allowedIds.has(String(v.id)));
        if (!permitted.length) return;

        const originalVariant = permitted.find(v => String(v.id) === String(original.value)) || permitted[0];
        const grid = document.createElement('div');
        grid.className = 'sese-opt-grid';
        grid.dataset.seseColumns = String(count);
        const dropdowns = [];
        const swatches = [];

        for (let index = 0; index < count; index++) {
          const cell = document.createElement('div');
          cell.className = 'sese-opt-cell';
          const dropdown = document.createElement('select');
          dropdown.className = 'sese-opt-dropdown';
          dropdown.setAttribute('aria-label', names[index] + ' ' + (rowIndex + 1));
          const choices = [...new Set(permitted.map(v => getParts(v)[index]))];
          choices.forEach(value => dropdown.add(new Option(value, value)));
          if (looksLikeColor(names[index])) {
            cell.classList.add('sese-opt-cell--color');
            const swatch = document.createElement('span');
            swatch.className = 'sese-opt-swatch';
            swatch.setAttribute('aria-hidden', 'true');
            cell.append(swatch);
            swatches[index] = swatch;
          }
          cell.append(dropdown);
          grid.append(cell);
          dropdowns.push(dropdown);
        }

        function syncDisplay(variant) {
          if (!variant) return;
          const parts = getParts(variant);
          dropdowns.forEach((dropdown, index) => {
            dropdown.value = parts[index];
            if (swatches[index]) {
              swatches[index].style.background = hexFor(parts[index]);
              swatches[index].style.borderColor = parts[index].trim().toLowerCase() === 'white'
                ? '#aaa' : 'rgba(0,0,0,.15)';
            }
          });
        }

        dropdowns.forEach((dropdown, changedIndex) => {
          dropdown.addEventListener('change', () => {
            const wanted = dropdowns.map(el => el.value);
            let chosen = permitted.find(variant =>
              getParts(variant).every((part, index) => part === wanted[index])
            );
            // If the combination doesn't exist, respect the option the user just changed,
            // then pick the nearest available combination and synchronize every dropdown.
            if (!chosen) {
              const candidates = permitted.filter(variant =>
                getParts(variant)[changedIndex] === wanted[changedIndex]
              );
              const score = variant => getParts(variant).reduce(
                (total, part, index) => total + (part === wanted[index] ? 1 : 0), 0
              );
              chosen = candidates.reduce((best, candidate) =>
                !best || score(candidate) > score(best) ? candidate : best, null);
            }
            if (!chosen) {
              syncDisplay(permitted.find(v => String(v.id) === String(original.value)) || originalVariant);
              return;
            }
            syncDisplay(chosen);
            if (String(original.value) !== String(chosen.id)) {
              original.value = String(chosen.id);
              // Important: the existing SESE code already listens here to update
              // image, bundle total and selected variant ID used by Add to Cart.
              original.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        });

        original.addEventListener('change', () => {
          syncDisplay(permitted.find(v => String(v.id) === String(original.value)) || originalVariant);
        });

        original.classList.add('sese-opt-native');
        original.tabIndex = -1;
        original.setAttribute('aria-hidden', 'true');
        field.classList.add('sese-opt-field');
        field.append(grid);
        row.dataset.seseSplitReady = 'true';
        syncDisplay(originalVariant);
        enhanced = true;
      });

      if (enhanced && !list.querySelector('.sese-opt-heading')) {
        const heading = document.createElement('div');
        heading.className = 'sese-opt-heading';
        heading.textContent = names.join(', ');
        list.prepend(heading);
      }
    });

    if (root.querySelector('.sese-opt-field')) root.dataset.seseSplitOptions = 'true';
    return true;
  }

  function boot() {
    document.querySelectorAll('[data-sese-bundle-builder]').forEach(enhanceRoot);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  // Theme editor inserts / re-renders the product information block dynamically.
  document.addEventListener('shopify:section:load', boot);
  window.addEventListener('load', boot);
  // Small bounded retries handle deferred initial core bundle initialization.
  [100, 350, 900, 1800].forEach(delay => setTimeout(boot, delay));
})();
